// Settings sections shared by the Support / Taxes / Parties tabs and the Settings dialog.
import { Fragment } from 'react';
import type { CceAllocation, CsMethod, HardshipLimitBase, MfsDeductionMethod, NetAdjustMethod, PartyIndex, SsMethod, Tactic, TacticSub, TimeshareMode } from '@divorceos/schema';
import { useCaseView } from '../caseView';
import {
  CCE_OPTIONS, FC4055_OPTIONS, LABEL_OPTIONS, NET_ADJ_OPTIONS, SS_OPTIONS, SUB_OPTIONS, TACTICS, cceDisplayLabel, fcWord, partyLabels, programDefaults,
  tacticLong, tacticUsesSub, type Fc4055Setting, type LabelPref, type Relation,
} from '../model';
import { useDis } from '../store';
import { ReleasePicker } from '../ReleasePicker';
import { Check, Frm, Hint, Num, OptNum, Sec, Sel, Two } from './form';
import { setStateAdj, stateAdjOf, setFederalAdj, federalAdjOf } from '../engineExt';

const P01: PartyIndex[] = [0, 1];

export function CsSettings() {
  const { c, labels, fcResolved, result, doc } = useCaseView();
  const update = useDis((s) => s.update);
  const setPrefs = useDis((s) => s.setPrefs);
  const d = programDefaults();
  const s = c.settings;
  const hf = doc.prefs.hardshipFindings;
  const fcv = (s.fc4055Version ?? 'PRE_SB343') as Fc4055Setting;
  return (
    <Sec title="Child support">
      <Frm>
        <Sel label="Method" value={s.csMethod} changed={s.csMethod !== d.csMethod} options={[['GUIDELINE', 'Guideline (FC 4055)'], ['USER', 'User amount']] as const} onChange={(v: CsMethod) => update((x) => { x.settings.csMethod = v; })} />
        {s.csMethod === 'USER' && (<>
          <label>User amount (monthly)</label>
          <div className="two">
            <Num label="User child support" value={c.userSupport.csAmount} onChange={(n) => update((x) => { x.userSupport.csAmount = n; })} />
            <select aria-label="Paid by" value={c.userSupport.csPayor} onChange={(e) => update((x) => { x.userSupport.csPayor = Number(e.target.value) as PartyIndex; })}>
              {P01.map((p) => <option key={p} value={p}>paid by {labels[p]}</option>)}
            </select>
          </div>
        </>)}
        <Sel label="Use FC 4055 as amended effective 1 Sep 2024" value={fcv} changed={fcv !== (d.fc4055Version ?? 'AUTO')}
          options={FC4055_OPTIONS.map(([k, l]) => [k, k === 'AUTO' ? `Automatic (now: ${fcResolved === 'SB343' ? 'Yes' : 'No'})` : k === 'SB343' ? 'Yes, from 1 Sep 2024' : 'No, the earlier version'] as const)}
          onChange={(v: Fc4055Setting) => update((x) => { x.settings.fc4055Version = v; })} />
        <Hint>Automatic means Yes for tax year 2025 and later, and for 2024 once 1 Sep 2024 has passed. This case: tax year {c.taxYear}, FC 4055 {fcWord(fcResolved)} 1 Sep 2024.{fcResolved === 'SB343' ? ' Under the amended version the low end of the low-income range is applied to presumed child support, and add-ons are prorated by net income (FC 4061(b)): a strict 50/50 split is not selectable under SB 343 in this version.' : ''}</Hint>
        <Sel label="Low-income adjustment" value={s.displayLowIncomeRange ? 'y' : 'n'} changed={s.displayLowIncomeRange !== d.displayLowIncomeRange} options={[['y', 'Show the range when it applies'], ['n', 'Hide the range']] as const} onChange={(v) => update((x) => { x.settings.displayLowIncomeRange = v === 'y'; })} />
        <Sel label="Low-income threshold CPI update" value={s.applyCpiToLowIncomeThreshold ? 'y' : 'n'} changed={s.applyCpiToLowIncomeThreshold !== d.applyCpiToLowIncomeThreshold} options={[['y', 'Apply the annual CPI figure'], ['n', 'Use the base figure']] as const} onChange={(v) => update((x) => { x.settings.applyCpiToLowIncomeThreshold = v === 'y'; })} />
        <Sel label="Add-on (child care) allocation" value={s.cceAllocation} changed={s.cceAllocation !== d.cceAllocation} options={CCE_OPTIONS} onChange={(v: CceAllocation) => update((x) => { x.settings.cceAllocation = v; })} />
        {s.cceAllocation === 'CUSTOM' && (<>
          <label>{labels[0]}’s share of add-ons (%)</label>
          <Num label={`${labels[0]} add-on share percent`} value={s.cceCustomPercent ?? 50} onChange={(n) => update((x) => { x.settings.cceCustomPercent = Math.min(100, Math.max(0, n)); })} />
          <Hint>{labels[0]} {Math.round(s.cceCustomPercent ?? 50)}% · {labels[1]} {100 - Math.round(s.cceCustomPercent ?? 50)}% of total add-ons.</Hint>
        </>)}
        {result && <Hint>Method actually used for this case: <b>{cceDisplayLabel(result.guideline.cceAllocationLabel)}</b>.</Hint>}
        <Sel label="Child support on adjusted nets" value={s.csOnAdjustedNets ? 'y' : 'n'} changed={s.csOnAdjustedNets !== d.csOnAdjustedNets} options={[['n', 'No, on unadjusted nets'], ['y', 'Yes, on adjusted nets']] as const} onChange={(v) => update((x) => { x.settings.csOnAdjustedNets = v === 'y'; })} />
        <Sel label="Hardship limit based on" value={s.hardshipLimitBase} changed={s.hardshipLimitBase !== d.hardshipLimitBase} options={[['PRESUMED_CS', 'Presumed child support'], ['BASIC_CS', 'Basic child support'], ['CS_ALLOCATION', 'Child support allocation']] as const} onChange={(v: HardshipLimitBase) => update((x) => { x.settings.hardshipLimitBase = v; })} />
        <label>Hardship limit (% per party)</label>
        <Two labels={labels}>
          <Num label={`Hardship limit ${labels[0]}`} value={s.hardshipLimitMultiplier[0]} onChange={(n) => update((x) => { x.settings.hardshipLimitMultiplier[0] = n; })} />
          <Num label={`Hardship limit ${labels[1]}`} value={s.hardshipLimitMultiplier[1]} onChange={(n) => update((x) => { x.settings.hardshipLimitMultiplier[1] = n; })} />
        </Two>
        <label>Hardship findings (FC 4072)</label>
        <input type="text" aria-label="Hardship reasons" placeholder="Facts and reasons for the hardship" value={hf.reasons} onChange={(e) => { const v = e.target.value; setPrefs((p) => { p.hardshipFindings.reasons = v; }); }} />
        <label>Hardship duration</label>
        <input type="text" aria-label="Hardship duration" placeholder="e.g. until June 2028, or while the second child is a minor" value={hf.duration} onChange={(e) => { const v = e.target.value; setPrefs((p) => { p.hardshipFindings.duration = v; }); }} />
        <Hint>FC 4072 requires the court to state the reasons for a hardship deduction and its duration. These print on the Findings report.</Hint>
        <label>Child support multiplier</label>
        <Num label="Child support multiplier" value={s.csMultiplier} className={s.csMultiplier !== d.csMultiplier ? 'chg' : ''} onChange={(n) => update((x) => { x.settings.csMultiplier = n; })} />
        <Sel label="Timeshare worksheet mode" value={s.timeshareMode} changed={s.timeshareMode !== d.timeshareMode} options={[['HOURS', 'Hours'], ['SANTA_CLARA', 'Santa Clara (quarter days)']] as const} onChange={(v: TimeshareMode) => update((x) => { x.settings.timeshareMode = v; })} />
      </Frm>
    </Sec>
  );
}

export function SsSettings() {
  const { c, labels } = useCaseView();
  const update = useDis((s) => s.update);
  const prefs = useDis((s) => s.docs.find((d) => d.key === s.active)?.prefs);
  const setPrefs = useDis((s) => s.setPrefs);
  const relation = prefs?.relation ?? 'married';
  const d = programDefaults();
  const s = c.settings;
  const f = c.userSupport.formula;
  const ssBasis = prefs?.ssTax.basis ?? 'post2018';
  const setSsBasis = (b: 'pre2019' | 'post2018' | 'modified') => {
    setPrefs((p) => { p.ssTax.basis = b; });
    update((x) => { x.settings.includeSsAfter2018InFederal = b === 'pre2019'; }); // only a pre-2019 unmodified order is federally deductible
  };
  // The order date drives both treatments: federal deducts only before 2019 (TCJA); California deducts only before 2026 (SB 711).
  const setOrderDate = (v: string) => {
    setPrefs((p) => { p.ssTax.orderDate = v; });
    if (v) {
      const fedDeductible = v < '2019-01-01';
      setPrefs((p) => { p.ssTax.basis = fedDeductible ? 'pre2019' : 'post2018'; });
      update((x) => { x.settings.includeSsAfter2018InFederal = fedDeductible; x.settings.caConformsAlimony = v >= '2026-01-01'; });
    }
  };
  return (
    <Sec title="Spousal support (temporary)">
      <Frm>
        <Sel label="County formula" value={s.ssMethod} changed={s.ssMethod !== d.ssMethod} disabled={relation === 'never'} options={SS_OPTIONS} onChange={(v: SsMethod) => update((x) => { x.settings.ssMethod = v; })} />
        {s.ssMethod === 'USER' && (<>
          <label>User amount (monthly)</label>
          <div className="two">
            <Num label="User spousal support" disabled={relation === 'never'} value={c.userSupport.ssAmount} onChange={(n) => update((x) => { x.userSupport.ssAmount = n; })} />
            <select aria-label="Paid by" value={c.userSupport.ssPayor} onChange={(e) => update((x) => { x.userSupport.ssPayor = Number(e.target.value) as PartyIndex; })}>
              {P01.map((p) => <option key={p} value={p}>paid by {labels[p]}</option>)}
            </select>
          </div>
        </>)}
        {s.ssMethod === 'FORMULA' && (<>
          <label>User formula</label>
          <div className="two">
            <span className="muted">% of higher net</span><span className="muted">% of lower net</span>
            <Num label="Percent of higher earner's net" value={f.addPercentHighEarner} onChange={(n) => update((x) => { x.userSupport.formula.addPercentHighEarner = n; })} />
            <Num label="Percent of lower earner's net" value={f.subtractPercentLowEarner} onChange={(n) => update((x) => { x.userSupport.formula.subtractPercentLowEarner = n; })} />
          </div>
          <Sel label="Formula adjustment for CS" value={f.adjust} options={NET_ADJ_OPTIONS} onChange={(v: NetAdjustMethod) => update((x) => { x.userSupport.formula.adjust = v; })} />
          <label>Formula multiplier</label>
          <Num label="Formula multiplier" value={f.multiplyBy} onChange={(n) => update((x) => { x.userSupport.formula.multiplyBy = n; })} />
        </>)}
        <Sel label="Adjust for child support" value={s.netAdjustMethod} changed={s.netAdjustMethod !== d.netAdjustMethod} options={NET_ADJ_OPTIONS} onChange={(v: NetAdjustMethod) => update((x) => { x.settings.netAdjustMethod = v; })} />
        <Sel label="Nets adjusted for the tax effect" value={s.ssOnAdjustedNets ? 'y' : 'n'} changed={s.ssOnAdjustedNets !== d.ssOnAdjustedNets} options={[['y', 'Adjusted nets'], ['n', 'Unadjusted nets']] as const} onChange={(v) => update((x) => { x.settings.ssOnAdjustedNets = v === 'y'; })} />
        <Sel label="Spousal support order date (federal tax)" value={ssBasis} changed={ssBasis !== 'post2018'}
          options={[['pre2019', 'Order/agreement before 1 Jan 2019'], ['post2018', 'Order/agreement 2019 or later'], ['modified', 'Pre-2019 order modified to adopt the new rule']] as const}
          onChange={(v) => setSsBasis(v)} />
        {prefs && <><label>Order / agreement date (optional)</label>
          <input type="date" aria-label="Spousal support order date" value={prefs.ssTax.orderDate} onChange={(e) => setOrderDate(e.target.value)} /></>}
        <Sel label="California spousal support tax (SB 711)" value={s.caConformsAlimony ? 'conform' : 'deduct'} changed={s.caConformsAlimony !== d.caConformsAlimony}
          options={[['conform', 'Order 1 Jan 2026 or later — not deductible/taxable for CA'], ['deduct', 'Order before 2026 — deductible/taxable for CA']] as const}
          onChange={(v) => update((x) => { x.settings.caConformsAlimony = v === 'conform'; })} />
        <Hint>Federal tax treatment follows the order date: alimony under orders made before 2019 is federally deductible/taxable; the TCJA made 2019-or-later orders (and pre-2019 orders modified to adopt the new rule) non-deductible. California did not conform until SB 711 (Ch. 231, Stats. 2025): orders made before 1 Jan 2026 are still deductible/taxable for California, but orders on or after 1 Jan 2026 conform to the federal rule and are not deductible or taxable for California either — so the after-tax cost to the payor equals the benefit to the payee.{s.domesticPartnership ? ' Registered domestic partners: deductible for California only (FC 297.5).' : ''}</Hint>
        <Sel label="Gross up for the recipient’s tax" value={s.bumpSs ? 'y' : 'n'} changed={s.bumpSs !== d.bumpSs} options={[['n', 'No'], ['y', 'Yes (recipient made whole for tax)']] as const} onChange={(v) => update((x) => { x.settings.bumpSs = v === 'y'; })} />
        <label>Spousal support multiplier</label>
        <Num label="Spousal support multiplier" value={s.ssMultiplier} className={s.ssMultiplier !== d.ssMultiplier ? 'chg' : ''} onChange={(n) => update((x) => { x.settings.ssMultiplier = n; })} />
        {relation === 'never' && <span className="hint" style={{ color: 'var(--amber)' }}>Spousal support is excluded because the parties never married or registered (Parties tab): the formula is set to a user amount of $0.</span>}
      </Frm>
    </Sec>
  );
}

const FC4320: [string, string][] = [
  ['a', 'Earning capacity & marketable skills; time and expense to acquire them'],
  ['b', 'Supported party’s contribution to the other’s education, training, career or license'],
  ['c', 'Supporting party’s ability to pay (earning capacity, income, assets, standard of living)'],
  ['d', 'Needs of each party based on the marital standard of living'],
  ['e', 'Obligations and assets, including separate property, of each party'],
  ['f', 'Duration of the marriage'],
  ['g', 'Supported party’s ability to work without unduly interfering with the children'],
  ['h', 'Age and health of the parties'],
  ['i', 'Documented history of domestic violence'],
  ['j', 'Immediate and specific tax consequences to each party'],
  ['k', 'Balance of the hardships to each party'],
  ['l', 'Goal that the supported party be self-supporting within a reasonable period'],
  ['m', 'Criminal conviction of an abusive spouse'],
  ['n', 'Any other just and equitable factors'],
];

export function LongTermSpousalSettings() {
  const prefs = useDis((s) => s.docs.find((d) => d.key === s.active)?.prefs);
  const setPrefs = useDis((s) => s.setPrefs);
  if (!prefs) return null;
  const lt = prefs.longTerm;
  return (
    <Sec title="Long-term spousal support (FC 4320)">
      <Frm>
        <Sel label="Assess long-term (permanent) support" value={lt.assessed ? 'y' : 'n'} changed={lt.assessed}
          options={[['n', 'Not assessed'], ['y', 'Assess the FC 4320 factors']] as const} onChange={(v) => setPrefs((p) => { p.longTerm.assessed = v === 'y'; })} />
      </Frm>
      {lt.assessed && (<>
        <Hint>Long-term (permanent) spousal support is determined on the FC 4320 factors — there is no statewide permanent-support formula. The temporary, county-guideline amount shown above does not set long-term support. Record each factor; the assessment prints on the Findings report, separate from the formula result.</Hint>
        <Frm>
          {FC4320.map(([k, label]) => (
            <Fragment key={k}>
              <label>({k}) {label}</label>
              <input type="text" aria-label={`FC 4320 factor ${k}`} value={lt.factors[k] ?? ''} onChange={(e) => { const v = e.target.value; setPrefs((p) => { p.longTerm.factors[k] = v; }); }} />
            </Fragment>
          ))}
          <label>Findings / tentative amount &amp; duration</label>
          <input type="text" aria-label="Long-term findings" value={lt.findings} placeholder="e.g. $X/mo for Y years, reviewable" onChange={(e) => { const v = e.target.value; setPrefs((p) => { p.longTerm.findings = v; }); }} />
        </Frm>
      </>)}
    </Sec>
  );
}

export function DepartureSettings() {
  const { labels } = useCaseView();
  const prefs = useDis((s) => s.docs.find((d) => d.key === s.active)?.prefs);
  const setPrefs = useDis((s) => s.setPrefs);
  if (!prefs) return null;
  const dep = prefs.departure;
  const set = (patch: Partial<typeof dep>) => setPrefs((p) => { Object.assign(p.departure, patch); });
  return (
    <Sec title="Court-ordered amount (departure from guideline)">
      <Frm>
        <Sel label="Order differs from the guideline" value={dep.applies ? 'y' : 'n'} changed={dep.applies}
          options={[['n', 'No — the guideline amount is ordered'], ['y', 'Yes — the court ordered a different amount']] as const}
          onChange={(v) => set({ applies: v === 'y' })} />
        {dep.applies && (<>
          <label>Ordered child support (monthly)</label>
          <div className="two"><Num label="Ordered child support" value={dep.orderedCs} onChange={(n) => set({ orderedCs: n })} /><span className="muted">paid by the higher earner</span></div>
          <label>Ordered spousal support (monthly)</label>
          <div className="two"><Num label="Ordered spousal support" value={dep.orderedSs} onChange={(n) => set({ orderedSs: n })} /><span className="muted">temporary</span></div>
          <label>Reasons the guideline is rebutted (FC 4057)</label>
          <input type="text" aria-label="Departure reasons" placeholder="e.g. stipulation under FC 4065; a special-circumstance factor" value={dep.reasons} onChange={(e) => { const v = e.target.value; set({ reasons: v }); }} />
          <Hint>The guideline amount is presumed correct (FC 4055). A court may order a different amount only on a rebuttal factor (FC 4057(b)), with a finding that the guideline would be unjust or inappropriate and that the order is in the children&rsquo;s best interest (FC 4053). disOS keeps the guideline figure and the ordered figure separate on the Findings report; this does not change the computed guideline.</Hint>
        </>)}
      </Frm>
    </Sec>
  );
}

export function ReimbursementSettings() {
  const { labels } = useCaseView();
  const prefs = useDis((s) => s.docs.find((d) => d.key === s.active)?.prefs);
  const setPrefs = useDis((s) => s.setPrefs);
  if (!prefs) return null;
  const list = prefs.reimbursements;
  const add = () => setPrefs((p) => { p.reimbursements.push({ date: '', payer: 0, category: '', amount: 0, notice: false, status: 'pending' }); });
  const del = (i: number) => setPrefs((p) => { p.reimbursements.splice(i, 1); });
  const set = (i: number, patch: Partial<(typeof list)[number]>) => setPrefs((p) => { Object.assign(p.reimbursements[i], patch); });
  return (
    <Sec title="Add-on reimbursements (FC 4063)">
      {list.length === 0 && <p className="muted" style={{ margin: '0 0 10px' }}>No reimbursement claims recorded.</p>}
      {list.map((r, i) => (
        <Frm key={i}>
          <label>Date</label>
          <input type="date" aria-label={`Reimbursement date ${i + 1}`} value={r.date} onChange={(e) => { const v = e.target.value; set(i, { date: v }); }} />
          <label>Paid by</label>
          <select aria-label={`Reimbursement payer ${i + 1}`} value={r.payer} onChange={(e) => set(i, { payer: Number(e.target.value) as PartyIndex })}>{P01.map((p) => <option key={p} value={p}>{labels[p]}</option>)}</select>
          <label>Category</label>
          <input type="text" aria-label={`Reimbursement category ${i + 1}`} placeholder="e.g. uninsured medical" value={r.category} onChange={(e) => { const v = e.target.value; set(i, { category: v }); }} />
          <label>Amount</label>
          <Num label={`Reimbursement amount ${i + 1}`} value={r.amount} onChange={(n) => set(i, { amount: n })} />
          <label>Status</label>
          <select aria-label={`Reimbursement status ${i + 1}`} value={r.status} onChange={(e) => set(i, { status: e.target.value as 'pending' | 'paid' | 'disputed' })}><option value="pending">Pending</option><option value="paid">Paid</option><option value="disputed">Disputed</option></select>
          <Check label="Written notice given within 30 days (FC 4063)" checked={r.notice} onChange={(b) => set(i, { notice: b })} />
          <label />
          <button type="button" className="btn sm" onClick={() => del(i)}>Remove</button>
        </Frm>
      ))}
      <div className="act" style={{ marginTop: 8 }}><button type="button" className="btn" onClick={add}>Add reimbursement</button></div>
      <Hint>Record a parent&rsquo;s actual payment of an add-on (for example uninsured medical). FC 4063 requires written notice within 30 days of payment and reimbursement of the other parent&rsquo;s share within 30 days; a charge may be disputed. Recorded claims print on the Findings report.</Hint>
    </Sec>
  );
}

export function TacticSettings() {
  const { c, labels, result } = useCaseView();
  const update = useDis((s) => s.update);
  const d = programDefaults();
  const pr = c.proposed;
  const t = pr.tactic;
  return (
    <Sec title="Proposed panel">
      <Frm>
        <Sel label="Tactic" value={t} changed={t !== '9'} options={TACTICS.map((x) => [x.id, `${x.id} · ${tacticLong(x.id, labels)}`] as const)} onChange={(v: Tactic) => update((x) => { x.proposed.tactic = v; })} />
        {tacticUsesSub(t) && <Sel label="Sub-option" value={pr.sub} changed={pr.sub !== 0} options={SUB_OPTIONS} onChange={(v: TacticSub) => update((x) => { x.proposed.sub = v; })} />}
        {(t === '2' || t === '3') && (<><label>Target net spendable (monthly)</label><Num label="Target net spendable" value={pr.targetAmount} onChange={(n) => update((x) => { x.proposed.targetAmount = n; })} /></>)}
        {['4', '5', '6', '7'].includes(t) && (<><label>Target percentage</label><Num label="Target percentage" value={pr.targetPercent} onChange={(n) => update((x) => { x.proposed.targetPercent = n; })} /></>)}
        {(t === '1' || (tacticUsesSub(t) && pr.sub >= 2 && pr.sub <= 4)) && (<>
          <label>Child support (nondeductible)</label>
          <div className="two">
            <Num label="Proposed child support" value={pr.userCs} onChange={(n) => update((x) => { x.proposed.userCs = n; })} />
            <select aria-label="Child support paid by" value={pr.userCsPayor} onChange={(e) => update((x) => { x.proposed.userCsPayor = Number(e.target.value) as PartyIndex; })}>{P01.map((p) => <option key={p} value={p}>paid by {labels[p]}</option>)}</select>
          </div>
          <label>Spousal support (deductible)</label>
          <div className="two">
            <Num label="Proposed spousal support" value={pr.userSs} onChange={(n) => update((x) => { x.proposed.userSs = n; })} />
            <select aria-label="Spousal support paid by" value={pr.userSsPayor} onChange={(e) => update((x) => { x.proposed.userSsPayor = Number(e.target.value) as PartyIndex; })}>{P01.map((p) => <option key={p} value={p}>paid by {labels[p]}</option>)}</select>
          </div>
        </>)}
        <Sel label="Dependency releases (Form 8332)" value={c.settings.releaseExemptions8332 ? 'y' : 'n'} changed={c.settings.releaseExemptions8332 !== d.releaseExemptions8332} options={[['y', 'Allowed: release when it lowers taxes'], ['n', 'Not allowed']] as const} onChange={(v) => update((x) => { x.settings.releaseExemptions8332 = v === 'y'; })} />
        <Hint>Without a selection below, the Proposed panel releases every available dependent when releases are allowed (now: {result ? result.proposed.releases : '—'}). The Exemption releases report compares the combinations of children.</Hint>
      </Frm>
      <h4 style={{ marginTop: 12 }}>Children released (Form 8332)</h4>
      <ReleasePicker />
    </Sec>
  );
}

export function TaxSettings() {
  const { c, labels } = useCaseView();
  const update = useDis((s) => s.update);
  const tsKeys = [['socialSecurity', 'Social Security tax'], ['medicare', 'Medicare tax'], ['selfEmploymentTax', 'Self-employment tax'], ['stateIncomeTax', 'California income tax'], ['stateDisabilityInsurance', 'California SDI']] as const;
  const crKeys = [['childTaxCredit', 'Child tax credit'], ['earnedIncomeCredit', 'Earned income credit'], ['childCareCredit', 'Child care credit (federal)'], ['caExemptionCredit', 'California exemption credits'], ['caJchhCredit', 'Joint custody head of household credit'], ['caRentCredit', 'Renter’s credit'], ['caChildCareCredit', 'California child care credit']] as const;
  return (
    <>
      <StateTaxSettings />
      <Sec title="Taxes included (per party)">
        <table className="tbl">
          <thead><tr><th>Tax</th><th>{labels[0]}</th><th>{labels[1]}</th></tr></thead>
          <tbody>
            {tsKeys.map(([k, l]) => (
              <tr key={k}><td>{l}</td>{P01.map((p) => <td key={p}><label><input type="checkbox" checked={c.parties[p].taxSettings[k]} onChange={(e) => update((x) => { x.parties[p].taxSettings[k] = e.target.checked; })} /> include</label></td>)}</tr>
            ))}
          </tbody>
        </table>
      </Sec>
      <Sec title="Credits included (per party)">
        <table className="tbl">
          <thead><tr><th>Credit</th><th>{labels[0]}</th><th>{labels[1]}</th></tr></thead>
          <tbody>
            {crKeys.map(([k, l]) => (
              <tr key={k}><td>{l}</td>{P01.map((p) => <td key={p}><label><input type="checkbox" checked={c.parties[p].creditOverride[k]} onChange={(e) => update((x) => { x.parties[p].creditOverride[k] = e.target.checked; })} /> include</label></td>)}</tr>
            ))}
          </tbody>
        </table>
      </Sec>
      <Sec title="Filing details">
        <table className="tbl">
          <thead><tr><th>Item</th><th>{labels[0]}</th><th>{labels[1]}</th></tr></thead>
          <tbody>
            <tr><td>Deductions when filing separately</td>{P01.map((p) => <td key={p}><select aria-label="MFS deduction" value={c.parties[p].mfsDeductionMethod} onChange={(e) => update((x) => { x.parties[p].mfsDeductionMethod = e.target.value as MfsDeductionMethod; })}><option value="STANDARD">Standard</option><option value="ITEMIZE">Itemize</option></select></td>)}</tr>
            <tr><td>Blind</td>{P01.map((p) => <td key={p}><input type="checkbox" checked={c.parties[p].taxInfo.blind} onChange={(e) => update((x) => { x.parties[p].taxInfo.blind = e.target.checked; })} /></td>)}</tr>
            <tr><td>Qualifies for the CA foster youth credit</td>{P01.map((p) => <td key={p}><input type="checkbox" checked={c.parties[p].taxInfo.qualifiesCaFytc} onChange={(e) => update((x) => { x.parties[p].taxInfo.qualifiesCaFytc = e.target.checked; })} /></td>)}</tr>
            <tr><td>Filing separately: lived together</td>{P01.map((p) => <td key={p}><input type="checkbox" checked={c.parties[p].taxInfo.mfsLivedTogether} onChange={(e) => update((x) => { x.parties[p].taxInfo.mfsLivedTogether = e.target.checked; })} /></td>)}</tr>
            <tr><td>Filing separately: qualifies for EITC</td>{P01.map((p) => <td key={p}><input type="checkbox" checked={c.parties[p].taxInfo.mfsQualifiedForEitc} onChange={(e) => update((x) => { x.parties[p].taxInfo.mfsQualifiedForEitc = e.target.checked; })} /></td>)}</tr>
            <tr><td>New spouse date of birth</td>{P01.map((p) => <td key={p}><input type="date" value={c.parties[p].taxInfo.newSpouseDateOfBirth ?? ''} onChange={(e) => update((x) => { x.parties[p].taxInfo.newSpouseDateOfBirth = e.target.value || undefined; })} /></td>)}</tr>
          </tbody>
        </table>
      </Sec>
    </>
  );
}

/** Per party: factor applied to the computed California income tax, or a user-calculated amount replacing it. */
export function StateTaxSettings() {
  const { c, labels } = useCaseView();
  const update = useDis((s) => s.update);
  const adj = [stateAdjOf(c.parties[0]), stateAdjOf(c.parties[1])] as const;
  const off = (p: PartyIndex) => !c.parties[p].taxSettings.stateIncomeTax;
  return (
    <Sec title="Tax adjustments (state & federal)">
      <Frm>
        <label>State tax adjustment factor (%)</label>
        <Two labels={labels}>
          {P01.map((p) => (
            <OptNum key={p} label={`State tax adjustment factor ${labels[p]}`} value={adj[p].factorPercent} placeholder="100" disabled={adj[p].userMonthlyAmount !== null || off(p)}
              className={adj[p].factorPercent !== 100 ? 'chg' : ''} onChange={(n) => update((x) => setStateAdj(x.parties[p], { factorPercent: n === null ? 100 : Math.max(0, n) }))} />
          )) as [JSX.Element, JSX.Element]}
        </Two>
        <Hint>Scales the computed California income tax, for example when part of the income is taxed by another state (California then credits tax paid elsewhere, R&amp;TC §18001). 100% means no adjustment; SDI is not affected. No adjustment applies to a party whose California income tax is switched off under Taxes included.</Hint>
        <label>User-calculated state tax (monthly)</label>
        <Two labels={labels}>
          {P01.map((p) => (
            <OptNum key={p} label={`User-calculated state tax ${labels[p]}`} value={adj[p].userMonthlyAmount} placeholder="computed" disabled={off(p)}
              className={adj[p].userMonthlyAmount !== null ? 'chg' : ''} onChange={(n) => update((x) => setStateAdj(x.parties[p], { userMonthlyAmount: n === null ? null : Math.max(0, n) }))} />
          )) as [JSX.Element, JSX.Element]}
        </Two>
        <Hint>Replaces the computed state income tax with your own monthly figure, for example for a party who lives and is taxed in another state. Leave blank to use the computed tax (times the factor). The adjusted tax also changes the federal deduction for state taxes. Figures are on the Tax Data tab.</Hint>
        {(off(0) || off(1)) && <Hint>California income tax is excluded for {off(0) && off(1) ? 'both parties' : labels[off(0) ? 0 : 1]} (Taxes included, below), so no adjustment applies there.</Hint>}
      </Frm>
      <Frm>
        <label>Federal tax adjustment factor (%)</label>
        <Two labels={labels}>
          {P01.map((p) => {
            const fa = federalAdjOf(c.parties[p]);
            return <OptNum key={p} label={`Federal tax adjustment factor ${labels[p]}`} value={fa.factorPercent} placeholder="100" disabled={fa.userMonthlyAmount !== null}
              className={fa.factorPercent !== 100 ? 'chg' : ''} onChange={(n) => update((x) => setFederalAdj(x.parties[p], { factorPercent: n === null ? 100 : Math.max(0, n) }))} />;
          }) as [JSX.Element, JSX.Element]}
        </Two>
        <label>User-calculated federal tax (monthly)</label>
        <Two labels={labels}>
          {P01.map((p) => {
            const fa = federalAdjOf(c.parties[p]);
            return <OptNum key={p} label={`User-calculated federal tax ${labels[p]}`} value={fa.userMonthlyAmount} placeholder="computed"
              className={fa.userMonthlyAmount !== null ? 'chg' : ''} onChange={(n) => update((x) => setFederalAdj(x.parties[p], { userMonthlyAmount: n === null ? null : Math.max(0, n) }))} />;
          }) as [JSX.Element, JSX.Element]}
        </Two>
        <Hint>Accepts an independently prepared federal income-tax figure in place of the computed one (TAX-07). Leave blank to use the computed tax (times the factor). Self-employment tax is not affected. Figures are on the Tax Data tab.</Hint>
      </Frm>
    </Sec>
  );
}

export function PartySettings() {
  const { c, labels } = useCaseView();
  const update = useDis((s) => s.update);
  const setUi = useDis((s) => s.setUi);
  const setPrefs = useDis((s) => s.setPrefs);
  const prefs = useDis((s) => s.docs.find((d) => d.key === s.active)?.prefs);
  if (!prefs) return null;
  const relation = prefs.relation;
  const setRelation = (r: Relation) => {
    setPrefs((p) => { p.relation = r; });
    update((x) => {
      x.settings.domesticPartnership = r === 'rdp';
      if (r === 'never') { x.settings.ssMethod = 'USER'; x.userSupport.ssAmount = 0; }
    });
  };
  const autoLabels = partyLabels(c, 'auto');
  const i = c.info;
  const biz = prefs.business;
  const setBiz = (p: PartyIndex, key: 'receipts' | 'expenses', n: number | null) => {
    const val = n === null ? 0 : n;
    setPrefs((pr) => { pr.business[key][p] = val; });
    if (biz.use[p]) {
      const r = key === 'receipts' ? val : biz.receipts[p];
      const e = key === 'expenses' ? val : biz.expenses[p];
      update((x) => { x.parties[p].selfEmployment = r - e; });
    }
  };
  const setBizUse = (p: PartyIndex, on: boolean) => {
    setPrefs((pr) => { pr.business.use[p] = on; });
    if (on) update((x) => { x.parties[p].selfEmployment = biz.receipts[p] - biz.expenses[p]; });
  };
  return (
    <>
      <Sec title="Business income worksheet (self-employment)">
        <Frm>
          <label>Gross business receipts (monthly)</label>
          <Two labels={labels}>
            {P01.map((p) => <OptNum key={p} label={`Gross receipts ${labels[p]}`} value={biz.receipts[p] || null} placeholder="0" onChange={(n) => setBiz(p, 'receipts', n)} />) as [JSX.Element, JSX.Element]}
          </Two>
          <label>Expenditures required for operation (monthly)</label>
          <Two labels={labels}>
            {P01.map((p) => <OptNum key={p} label={`Operating expenses ${labels[p]}`} value={biz.expenses[p] || null} placeholder="0" onChange={(n) => setBiz(p, 'expenses', n)} />) as [JSX.Element, JSX.Element]}
          </Two>
          <label>Use worksheet net as self-employment income</label>
          <Two labels={labels}>
            {P01.map((p) => <Check key={p} label={`net $${Math.round(biz.receipts[p] - biz.expenses[p]).toLocaleString('en-US')}`} checked={biz.use[p]} onChange={(on) => setBizUse(p, on)} />) as [JSX.Element, JSX.Element]}
          </Two>
          <Hint>Self-employment income is gross receipts less the expenditures required to operate the business (FC 4058(a)(2)). Turn on &ldquo;Use worksheet net&rdquo; to set the Self-employment income row from this worksheet; the receipts and expenses are kept with the case.</Hint>
        </Frm>
      </Sec>
      <Sec title="Relationship and labels">
        <Frm>
          <Sel label="Relationship" value={relation} changed={relation !== 'married'} options={[['married', 'Married'], ['rdp', 'Registered domestic partners'], ['never', 'Never married (parentage)']] as const} onChange={setRelation} />
          {relation === 'rdp' && <Hint>Registered domestic partners: support is deductible on the California return but not federally, because federal law does not treat partners as spouses (FC 297.5). The engine applies this when domestic partnership is on.</Hint>}
          <Sel label="Same-sex marriage" value={c.settings.sameSexMarriage ? 'y' : 'n'} options={[['n', 'No'], ['y', 'Yes']] as const} onChange={(v) => update((x) => { x.settings.sameSexMarriage = v === 'y'; })} />
          <Sel label="Party labels" value={prefs.labels} changed={prefs.labels !== 'auto'} options={LABEL_OPTIONS.map(([k, l]) => [k, k === 'auto' ? `Automatic (now: ${autoLabels[0]} / ${autoLabels[1]})` : l] as const)} onChange={(v: LabelPref) => setPrefs((p) => { p.labels = v; })} />
          <label>Names</label>
          <Two labels={labels}>
            <input type="text" aria-label="Name A" value={i.partyNames[0]} placeholder="Party A" onChange={(e) => update((x) => { x.info.partyNames[0] = e.target.value; })} />
            <input type="text" aria-label="Name B" value={i.partyNames[1]} placeholder="Party B" onChange={(e) => update((x) => { x.info.partyNames[1] = e.target.value; })} />
          </Two>
          <Sel label="Petitioner" value={i.petitioner === null ? 'n' : String(i.petitioner)} options={[['n', 'Not set'], ['0', labels[0]], ['1', labels[1]]] as const} onChange={(v) => update((x) => { x.info.petitioner = v === 'n' ? null : (Number(v) as PartyIndex); })} />
        </Frm>
      </Sec>
      <Sec title="Attorneys">
        <Frm>
          <label>Attorney for {labels[0]}</label>
          <input type="text" value={i.attorneys[0]} onChange={(e) => update((x) => { x.info.attorneys[0] = e.target.value; })} />
          <label>Attorney for {labels[1]}</label>
          <input type="text" value={i.attorneys[1]} onChange={(e) => update((x) => { x.info.attorneys[1] = e.target.value; })} />
        </Frm>
      </Sec>
      <Sec title="Court caption (bench report and orders)">
        <Frm>
          <label>Attorney or party (name, bar no., address)</label>
          <input type="text" value={i.agencyName} onChange={(e) => update((x) => { x.info.agencyName = e.target.value; })} />
          <label>Telephone</label>
          <input type="text" value={i.telephone} onChange={(e) => update((x) => { x.info.telephone = e.target.value; })} />
          <label>Attorney for (name)</label>
          <input type="text" value={prefs.attyFor} onChange={(e) => setPrefs((p) => { p.attyFor = e.target.value; })} />
          <label>County</label>
          <input type="text" value={i.county} onChange={(e) => update((x) => { x.info.county = e.target.value; })} />
          <label>Street address</label>
          <input type="text" value={i.streetAddress} onChange={(e) => update((x) => { x.info.streetAddress = e.target.value; })} />
          <label>Mailing address</label>
          <input type="text" value={prefs.mailing} placeholder="Same as street address" onChange={(e) => setPrefs((p) => { p.mailing = e.target.value; })} />
          <label>City</label>
          <input type="text" value={i.city} onChange={(e) => update((x) => { x.info.city = e.target.value; })} />
          <label>ZIP code</label>
          <input type="text" value={i.zip} onChange={(e) => update((x) => { x.info.zip = e.target.value; })} />
          <label>Branch name</label>
          <input type="text" value={i.branch} onChange={(e) => update((x) => { x.info.branch = e.target.value; })} />
          <label>Case number</label>
          <input type="text" value={i.caseNumber} onChange={(e) => update((x) => { x.info.caseNumber = e.target.value; })} />
          <label>Report footer lines</label>
          <div className="two" style={{ gridTemplateColumns: '1fr' }}>
            <input type="text" aria-label="Footer line 1" value={i.customString1} onChange={(e) => update((x) => { x.info.customString1 = e.target.value; })} />
            <input type="text" aria-label="Footer line 2" value={i.customString2} onChange={(e) => update((x) => { x.info.customString2 = e.target.value; })} />
          </div>
          <Hint>“Attorney for” and the mailing address are saved with the case (disOS data in the case file; the other apps ignore them).</Hint>
        </Frm>
      </Sec>
      <Sec title="Client questionnaire">
        <p className="muted" style={{ margin: '0 0 8px' }}>Send the client a spreadsheet to fill in. When it comes back, disOS shows every changed field before applying it.</p>
        <button type="button" className="btn sm" onClick={() => setUi({ dialog: 'questionnaire' })}>Client questionnaire…</button>
      </Sec>
    </>
  );
}

export function ProgramSettings() {
  const ui = useDis((s) => s.ui);
  const setUi = useDis((s) => s.setUi);
  const setPrefs = useDis((s) => s.setPrefs);
  const firm = useDis((s) => s.docs.find((d) => d.key === s.active)?.prefs.report.firm ?? '');
  return (
    <Sec title="Program">
      <Frm>
        <label>Auto-recalculate</label>
        <div className="seg"><button type="button" className={ui.autoRecalc ? 'on' : ''} onClick={() => setUi({ autoRecalc: true })}>On</button><button type="button" className={!ui.autoRecalc ? 'on' : ''} onClick={() => setUi({ autoRecalc: false })}>Off (F9)</button></div>
        <label>Field tooltips</label>
        <div className="seg"><button type="button" className={ui.tooltips ? 'on' : ''} onClick={() => setUi({ tooltips: true })}>On</button><button type="button" className={!ui.tooltips ? 'on' : ''} onClick={() => setUi({ tooltips: false })}>Off</button></div>
        <label>Zero rows in the grid</label>
        <div className="seg"><button type="button" className={!ui.hideZeroGrid ? 'on' : ''} onClick={() => setUi({ hideZeroGrid: false })}>Show</button><button type="button" className={ui.hideZeroGrid ? 'on' : ''} onClick={() => setUi({ hideZeroGrid: true })}>Hide</button></div>
        <label>Firm footer on reports</label>
        <input type="text" value={firm} onChange={(e) => setPrefs((p) => { p.report.firm = e.target.value; })} />
        <Check label="Check FC 4055 inputs (warnings)" checked={false} disabled onChange={() => undefined} />
      </Frm>
    </Sec>
  );
}
